import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  ArrowForward,
  ChevronRight,
  ErrorOutlineRounded,
  RefreshRounded,
  Search,
  SearchOffRounded,
} from '@mui/icons-material';
import type { DialogProps } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { SuperSearchPageResult, SuperSearchResult } from 'api';
import { baseFirmUrl, companySummaryUrl } from 'common/config/urls';
import { FULL_ACCESS_VIEWER_VALUE } from 'common/constants/user';
import { normalizePinnedItems, PINNED_COMPANIES_KEY, PINNED_FIRMS_KEY } from 'common/constants/userPreferences';
import type { PinnedItemId } from 'common/constants/userPreferences';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useExtraPageActionVisibility, useFirmScopedPermissions, useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import type { FirmCompany } from 'common/types/company';
import type { Firm } from 'common/types/firm';
import type { UseStoreValues } from 'common/types/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { OverflowTooltip, useGuidedTour } from 'components/ui';
import { useDebouncedValue } from 'hooks';
import { COMPANY_NAVIGATION_ROUTES } from 'pages/Company/companyNavigation';
import { PrismThemeProvider } from 'theme';
import { shortDate } from 'utilities';
import { useUserUtils } from 'utilities/userUtils';
import { getSuperSearchCommandSections } from './SuperSearch.commands';
import { getSuperSearchResultIcon } from './SuperSearch.icons';
import {
  AssistiveText,
  ContextButton,
  ContextCurrent,
  ContextRow,
  ContextSeparator,
  EmptyActionButton,
  EmptyActions,
  EmptyDescription,
  EmptyIcon,
  EmptyState,
  EmptyTitle,
  ErrorDescription,
  ErrorIcon,
  ErrorRetryButton,
  ErrorState,
  ErrorTitle,
  GhostPrefix,
  GhostText,
  KeyCap,
  LoadingProgress,
  NavigationCancel,
  NavigationContext,
  NavigationIcon,
  NavigationOverlay,
  NavigationProgress,
  NavigationTarget,
  NavigationTitle,
  ResultButton,
  ResultCopy,
  ResultIcon,
  ResultLabel,
  ResultMeta,
  ResultsList,
  ResultsRoot,
  ResultSubtitle,
  ResultSubtitleSegment,
  ResultTypeBadge,
  SearchGuidance,
  SearchHeader,
  SearchInput,
  SearchInputRow,
  SearchLoadingLabel,
  SearchLoadingState,
  SearchSkeletonCopy,
  SearchSkeletonIcon,
  SearchSkeletonLine,
  SearchSkeletonRow,
  SearchTextField,
  SectionLabel,
  SectionRoot,
  StyledDialog,
} from './SuperSearch.styles';
import type { SuperSearchTone } from './SuperSearch.styles';
import { SUPER_SEARCH_TEST_IDS } from './SuperSearch.testIds';
import type {
  SuperSearchContext,
  SuperSearchDisplayItem,
  SuperSearchPendingPage,
  SuperSearchProps,
  SuperSearchShortcut,
} from './SuperSearch.types';
import {
  getGhostCompletion,
  getLocalSuperSearchEntityResults,
  getLocalSuperSearchMeasurementDateResults,
  getLocalSuperSearchPageResults,
  getSuperSearchNavigationPath,
  getSuperSearchSections,
  isSuperSearchSavedDestinationResult,
  matchesSuperSearchQuery,
  mergeSuperSearchResults,
} from './SuperSearch.utils';
import SuperSearchFooter from './SuperSearchFooter';
import useSuperSearchActions from './useSuperSearchActions';
import useSuperSearchQuery from './useSuperSearchQuery';
import {
  PRIMARY_NAV_INCLUDED_COMPANY_TYPES,
  PRIMARY_NAV_ITEMS,
} from '../PrimaryNavigation/PrimaryNavigation.constants';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_REMOTE_SEARCH_LENGTH = 3;
const MIN_DOCUMENT_SEARCH_LENGTH = 4;
const MIN_SAVED_DESTINATION_SEARCH_LENGTH = 3;
const NAVIGATION_DELAY_MS = 1100;
const QUICK_ACCESS_RESULT_LIMIT = 6;
const QUICK_ACCESS_RESULTS_PER_SECTION = 2;
const SEARCH_SKELETON_ROWS = ['primary', 'secondary', 'tertiary'] as const;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const getResultsAnnouncement = ({
  hasError,
  isLoading,
  quickAccessCount,
  resultCount,
}: {
  hasError: boolean;
  isLoading: boolean;
  quickAccessCount: number;
  resultCount: number;
}) => {
  if (isLoading) {
    return 'Searching Super Search.';
  }

  if (hasError) {
    return 'Super Search could not load. Please try again.';
  }

  if (resultCount === 0) {
    if (quickAccessCount > 0) {
      return `No exact matches. ${quickAccessCount} quick access ${quickAccessCount === 1 ? 'destination is' : 'destinations are'} available.`;
    }

    return 'No matching Super Search results.';
  }

  return `${resultCount} ${resultCount === 1 ? 'result' : 'results'} available. Use the Up and Down Arrow keys to navigate.`;
};

const getResultTone = (item: SuperSearchDisplayItem, sectionId: string): SuperSearchTone => {
  if (item.companyAction) {
    return 'companyAction';
  }

  if (sectionId === 'firm-actions' || sectionId === 'guided-tours') {
    return 'firmAction';
  }

  if (sectionId === 'firm-settings') {
    return 'firmSettings';
  }

  if (item.result.type === SuperSearchResult.type.MEASUREMENT_DATE) {
    return 'measurementDate';
  }

  if (item.result.type === SuperSearchResult.type.DOCUMENT) {
    return 'document';
  }

  if (item.result.type === SuperSearchResult.type.CAP_TABLE_VERSION) {
    return 'version';
  }

  if (
    item.result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION ||
    item.result.type === SuperSearchResult.type.VALUATION_VERSION
  ) {
    return 'version';
  }

  if (item.result.type === SuperSearchResult.type.WATERFALL_VIEW) {
    return 'companyPage';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH) {
    return 'companyAction';
  }

  if (item.result.type === SuperSearchResult.type.CUSTOM_VIEW) {
    return 'firmPage';
  }

  if (item.result.type === SuperSearchResult.type.FIRM) {
    return 'firm';
  }

  if (item.result.type === SuperSearchResult.type.COMPANY) {
    return 'company';
  }

  if (item.result.type === SuperSearchResult.type.FIRM_PAGE) {
    return 'firmPage';
  }

  return 'companyPage';
};

const getSectionTone = (sectionId: string): SuperSearchTone => {
  switch (sectionId) {
    case 'firms':
      return 'firm';
    case 'companies':
      return 'company';
    case 'firm-actions':
    case 'guided-tours':
      return 'firmAction';
    case 'company-actions':
      return 'companyAction';
    case 'measurement-dates':
    case 'switch-measurement-date':
      return 'measurementDate';
    case 'documents':
      return 'document';
    case 'cap-table-versions':
    case 'financials-versions':
    case 'valuation-versions':
      return 'version';
    case 'waterfall-views':
      return 'companyPage';
    case 'valuation-approaches':
      return 'companyAction';
    case 'custom-views':
      return 'firmPage';
    case 'firm-pages':
      return 'firmPage';
    case 'firm-settings':
      return 'firmSettings';
    case 'company-pages':
      return 'companyPage';
    default:
      return 'neutral';
  }
};

const getResultTypeLabel = (item: SuperSearchDisplayItem) => {
  if (item.typeLabel) {
    return item.typeLabel;
  }

  if (item.kind === 'guided-tour') {
    return 'Guided tour';
  }

  if (item.kind === 'command') {
    return 'Firm action';
  }

  if (item.kind === 'firm-setting') {
    return 'Firm setting';
  }

  if (item.kind === 'company-page-command') {
    return 'Company page';
  }

  if (item.kind === 'firm-page-command') {
    return 'Firm page';
  }

  if (item.kind === 'company-action') {
    return 'Company action';
  }

  if (item.result.type === SuperSearchResult.type.MEASUREMENT_DATE) {
    return 'Measurement date';
  }

  if (item.result.type === SuperSearchResult.type.DOCUMENT) {
    return 'Document';
  }

  if (item.result.type === SuperSearchResult.type.CAP_TABLE_VERSION) {
    return 'Cap Table version';
  }

  if (item.result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION) {
    return 'Financials version';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_VERSION) {
    return 'Valuation version';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH) {
    return 'Valuation approach';
  }

  if (item.result.type === SuperSearchResult.type.WATERFALL_VIEW) {
    return 'Waterfall view';
  }

  if (item.result.type === SuperSearchResult.type.CUSTOM_VIEW) {
    return item.result.custom_view?.page_type === 'valuations' ? 'Firm Valuations view' : 'Intelligence view';
  }

  return item.result.type.replace('_', ' ');
};

interface PendingNavigation {
  context: string;
  label: string;
  tone: SuperSearchTone;
  typeLabel: string;
}

const getNavigationContext = (item: SuperSearchDisplayItem) => {
  if (item.kind !== 'result') {
    return item.subtitle;
  }

  if (item.result.type === SuperSearchResult.type.DOCUMENT) {
    return item.subtitle;
  }

  if (item.result.company) {
    return `${item.result.company.name} · ${item.result.firm.name}`;
  }

  return item.result.firm.name;
};

const getSubtitleSegmentTone = (
  item: SuperSearchDisplayItem,
  segment: string,
  segmentIndex: number
): SuperSearchTone => {
  if (item.companyAction && segment === item.page?.label) {
    return 'companyPage';
  }

  if (segment === item.result.company?.name) {
    return 'company';
  }

  if (item.result.measurement_date && /\d{2}\/\d{2}\/\d{4}/.test(segment)) {
    return 'measurementDate';
  }

  if (segment === item.result.firm.name) {
    return 'firm';
  }

  const isVersionResult =
    item.result.type === SuperSearchResult.type.CAP_TABLE_VERSION ||
    item.result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION ||
    item.result.type === SuperSearchResult.type.VALUATION_VERSION;
  const isApproachVersion = item.result.type === SuperSearchResult.type.VALUATION_APPROACH && segmentIndex === 1;

  if ((isVersionResult && segmentIndex === 0) || isApproachVersion) {
    return 'version';
  }

  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH && segmentIndex === 0) {
    return 'companyAction';
  }

  return 'neutral';
};

const getSubtitleTonePriority = (item: SuperSearchDisplayItem, originalIndex: number, tone: SuperSearchTone) => {
  if (item.result.type === SuperSearchResult.type.VALUATION_APPROACH && originalIndex <= 1) {
    return originalIndex;
  }

  switch (tone) {
    case 'version':
      return 0;
    case 'companyPage':
      return 1;
    case 'companyAction':
    case 'neutral':
      return 2;
    case 'measurementDate':
      return 3;
    case 'company':
      return 4;
    case 'firm':
      return 5;
    default:
      return 6;
  }
};

const getOrderedSubtitleSegments = (item: SuperSearchDisplayItem) =>
  item.subtitle
    .split(' / ')
    .map((segment, originalIndex) => {
      const tone = getSubtitleSegmentTone(item, segment, originalIndex);
      const formattedSegment =
        tone === 'version' && ISO_DATE_PATTERN.test(segment) ? (shortDate(segment) ?? segment) : segment;

      return {
        originalIndex,
        segment: formattedSegment,
        tone,
      };
    })
    .sort(
      (left, right) =>
        getSubtitleTonePriority(item, left.originalIndex, left.tone) -
        getSubtitleTonePriority(item, right.originalIndex, right.tone)
    );

const canScopeItem = (item: SuperSearchDisplayItem, pendingPage?: SuperSearchPendingPage) =>
  item.kind === 'company-page-command' ||
  item.kind === 'firm-page-command' ||
  item.result.type === SuperSearchResult.type.FIRM ||
  (item.result.type === SuperSearchResult.type.COMPANY && Boolean(item.result.company)) ||
  (pendingPage?.scope === SuperSearchPageResult.scope.FIRM && item.result.type === SuperSearchResult.type.FIRM_PAGE) ||
  (pendingPage?.scope === SuperSearchPageResult.scope.COMPANY && Boolean(item.result.company));

const SuperSearch = ({ initialContext, onClose, open, zoomLevel }: SuperSearchProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { availableTours, startTour } = useGuidedTour();
  const { isStaffUser, isSuperUser } = useUserUtils();
  const [{ companyList, companyMeasurementDateList, currentMeasurementDate, firmList }] =
    useStore() as unknown as UseStoreValues;
  const {
    canAccessAuditLogs,
    canAccessFirmSettings,
    canAccessUserManagement,
    canCreateCompany,
    canCreateMeasurementDate,
    canMutateFirmScopedEntities,
  } = useFirmScopedPermissions();
  const actionVisibility = useExtraPageActionVisibility({
    bulkActions: canMutateFirmScopedEntities,
    pdfExport: isStaffUser() || isSuperUser(),
  });
  const [, checkFeature] = useCheckFeatures();
  const [context, setContext] = useState<SuperSearchContext>(initialContext);
  const {
    canMutateFirmScopedEntities: canMutateCompany,
    currentFirmRole: companyFirmRole,
    isRestrictedUser: isCompanyFirmRestricted,
  } = useFirmScopedPermissions({ firmId: context.firm?.id });
  const canOpenProForma =
    canMutateCompany || (!isCompanyFirmRestricted && companyFirmRole === FULL_ACCESS_VIEWER_VALUE);
  const [pendingPage, setPendingPage] = useState<SuperSearchPendingPage>();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation>();
  const [pressedShortcut, setPressedShortcut] = useState<SuperSearchShortcut>();
  const [pinnedCompaniesRaw] = useLocalStorageByUser<PinnedItemId[]>(PINNED_COMPANIES_KEY, []);
  const [pinnedFirmsRaw] = useLocalStorageByUser<PinnedItemId[]>(PINNED_FIRMS_KEY, []);
  const resultsRootRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);
  const navigationCancelRef = useRef<HTMLButtonElement>(null);
  const navigationTimerRef = useRef<number | null>(null);
  const shortcutFeedbackTimerRef = useRef<number | null>(null);
  const cancelPendingNavigation = useCallback(() => {
    if (navigationTimerRef.current !== null) {
      window.clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }

    setPendingNavigation(undefined);
  }, []);
  const flashShortcut = useCallback((shortcut: SuperSearchShortcut) => {
    if (shortcutFeedbackTimerRef.current !== null) {
      window.clearTimeout(shortcutFeedbackTimerRef.current);
    }

    setPressedShortcut(shortcut);
    shortcutFeedbackTimerRef.current = window.setTimeout(() => {
      setPressedShortcut(undefined);
      shortcutFeedbackTimerRef.current = null;
    }, 160);
  }, []);
  const pinnedCompanyIds = useMemo(
    () => new Set(normalizePinnedItems(pinnedCompaniesRaw).map(item => item.value)),
    [pinnedCompaniesRaw]
  );
  const pinnedFirmIds = useMemo(
    () => new Set(normalizePinnedItems(pinnedFirmsRaw).map(item => item.value)),
    [pinnedFirmsRaw]
  );
  const firmIconUrlsById = useMemo(
    () =>
      new Map(
        ((Array.isArray(firmList) ? firmList : []) as Firm[]).flatMap(firm =>
          typeof firm.id === 'number' && firm.icon_url ? [[firm.id, firm.icon_url] as const] : []
        )
      ),
    [firmList]
  );
  const normalizedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(normalizedQuery, SEARCH_DEBOUNCE_MS);
  const localSearchEntities = useMemo(() => {
    const activeFirm = context.firm;
    const firms = ((Array.isArray(firmList) ? firmList : []) as Firm[])
      .filter(firm => !activeFirm || firm.id === activeFirm.id)
      .map(firm => ({
        id: firm.id,
        name: firm.name,
        path: baseFirmUrl(firm.slug),
        slug: firm.slug,
      }))
      .sort((left, right) => {
        const pinnedDifference = Number(pinnedFirmIds.has(right.id)) - Number(pinnedFirmIds.has(left.id));
        if (pinnedDifference !== 0) {
          return pinnedDifference;
        }

        const currentDifference = Number(right.id === activeFirm?.id) - Number(left.id === activeFirm?.id);
        return currentDifference || left.name.localeCompare(right.name);
      });
    const companies =
      activeFirm && activeFirm.id === initialContext.firm?.id
        ? ((Array.isArray(companyList) ? companyList : []) as FirmCompany[])
          .filter(company => !company.company_type || PRIMARY_NAV_INCLUDED_COMPANY_TYPES.has(company.company_type))
          .map(company => ({
            firmId: activeFirm.id,
            id: company.company_id,
            name: company.company_name,
            path: companySummaryUrl(activeFirm.slug, company.company_slug),
            slug: company.company_slug,
          }))
          .sort((left, right) => {
            const pinnedDifference = Number(pinnedCompanyIds.has(right.id)) - Number(pinnedCompanyIds.has(left.id));
            if (pinnedDifference !== 0) {
              return pinnedDifference;
            }

            const leftIsCurrentCompany = left.id === context.company?.id;
            const rightIsCurrentCompany = right.id === context.company?.id;
            const currentDifference = Number(rightIsCurrentCompany) - Number(leftIsCurrentCompany);
            return currentDifference || left.name.localeCompare(right.name);
          })
        : [];

    return { companies, firms };
  }, [
    companyList,
    context.company?.id,
    context.firm,
    firmList,
    initialContext.firm?.id,
    pinnedCompanyIds,
    pinnedFirmIds,
  ]);
  const localEntityResults = useMemo(
    () =>
      getLocalSuperSearchEntityResults({
        ...localSearchEntities,
        query: normalizedQuery,
      }),
    [localSearchEntities, normalizedQuery]
  );
  const quickAccessEntityResults = useMemo(
    () =>
      getLocalSuperSearchEntityResults({
        ...localSearchEntities,
        query: '',
      }),
    [localSearchEntities]
  );
  const localMeasurementDateSearchData = useMemo(() => {
    if (!context.firm) {
      return undefined;
    }

    const companies = (Array.isArray(companyList) ? companyList : []) as FirmCompany[];
    const currentCompany = companies.find(company => company.company_id === context.company?.id);
    const currentCompanyDates = context.company
      ? (companyMeasurementDateList ?? currentCompany?.company_measurement_dates ?? [])
      : companies.flatMap(company => company.company_measurement_dates ?? []);
    const measurementDatesByDate = new Map(
      currentCompanyDates.flatMap(measurementDate => {
        if (typeof measurementDate.id !== 'number' || !measurementDate.slug) {
          return [];
        }

        return [
          [
            measurementDate.date,
            {
              date: measurementDate.date,
              id: measurementDate.id,
              name: 'name' in measurementDate ? measurementDate.name : measurementDate.date,
              slug: measurementDate.slug,
            },
          ] as const,
        ];
      })
    );
    const path = context.company
      ? companySummaryUrl(context.firm.slug, context.company.slug)
      : `${baseFirmUrl(context.firm.slug)}/intelligence/summaries/firm-summary`;

    return {
      context,
      measurementDates: Array.from(measurementDatesByDate.values()),
      path,
    };
  }, [companyList, companyMeasurementDateList, context]);
  const localMeasurementDateResults = useMemo(() => {
    if (!localMeasurementDateSearchData) {
      return [];
    }

    return getLocalSuperSearchMeasurementDateResults({
      ...localMeasurementDateSearchData,
      query: normalizedQuery,
    });
  }, [localMeasurementDateSearchData, normalizedQuery]);
  const quickAccessMeasurementDateResults = useMemo(() => {
    if (!localMeasurementDateSearchData) {
      return [];
    }

    return getLocalSuperSearchMeasurementDateResults({
      ...localMeasurementDateSearchData,
      query: '',
    });
  }, [localMeasurementDateSearchData]);
  const localPageSearchData = useMemo(() => {
    if (!context.firm || pendingPage) {
      return undefined;
    }

    const { firm } = context;
    const firmPages = PRIMARY_NAV_ITEMS.map(page => ({
      key: page.slug,
      label: page.label,
      path: `${baseFirmUrl(firm.slug)}/${page.slug}`,
    }));
    const companyPages = COMPANY_NAVIGATION_ROUTES.filter(
      page => !page.hidden && (!page.featureFlag || checkFeature(page.featureFlag))
    ).map(page => ({
      key: page.slug,
      label: page.title,
      path: context.company ? page.route(firm.slug, context.company.slug) : baseFirmUrl(firm.slug),
      requiredFeatureFlag: page.featureFlag,
    }));

    return {
      companyPages,
      context,
      firmPages,
    };
  }, [checkFeature, context, pendingPage]);
  const localPageResults = useMemo(() => {
    if (!localPageSearchData) {
      return [];
    }

    return getLocalSuperSearchPageResults({
      ...localPageSearchData,
      query: normalizedQuery,
    });
  }, [localPageSearchData, normalizedQuery]);
  const quickAccessPageResults = useMemo(() => {
    if (!localPageSearchData) {
      return [];
    }

    return getLocalSuperSearchPageResults({
      ...localPageSearchData,
      query: '',
    });
  }, [localPageSearchData]);
  const localResultIds = useMemo(
    () =>
      new Set([...localEntityResults, ...localMeasurementDateResults, ...localPageResults].map(result => result.id)),
    [localEntityResults, localMeasurementDateResults, localPageResults]
  );
  const localPageTypes = useMemo(() => new Set(localPageResults.map(result => result.type)), [localPageResults]);
  const quickAccessResults = useMemo(
    () => mergeSuperSearchResults(quickAccessPageResults, quickAccessMeasurementDateResults, quickAccessEntityResults),
    [quickAccessEntityResults, quickAccessMeasurementDateResults, quickAccessPageResults]
  );
  const quickAccessResultIds = useMemo(
    () => new Set(quickAccessResults.map(result => result.id)),
    [quickAccessResults]
  );
  const quickAccessPageTypes = useMemo(
    () => new Set(quickAccessPageResults.map(result => result.type)),
    [quickAccessPageResults]
  );
  const activeMeasurementDate = useMemo(
    () =>
      new URLSearchParams(location.search).get('date') ??
      currentMeasurementDate?.date ??
      companyMeasurementDateList?.[0]?.date,
    [companyMeasurementDateList, currentMeasurementDate?.date, location.search]
  );
  const apiQuery = [pendingPage?.searchLabel ?? pendingPage?.label, debouncedQuery].filter(Boolean).join(' ');
  const requestKey = [pendingPage?.searchLabel ?? pendingPage?.label, normalizedQuery].filter(Boolean).join(' ');
  const isQueryDebouncing = normalizedQuery !== debouncedQuery;
  const isSearchEnabled = open && !isQueryDebouncing && apiQuery.length >= MIN_REMOTE_SEARCH_LENGTH;
  const includeDocuments = !pendingPage && debouncedQuery.length >= MIN_DOCUMENT_SEARCH_LENGTH;
  const includeSavedDestinations = !pendingPage && debouncedQuery.length >= MIN_SAVED_DESTINATION_SEARCH_LENGTH;
  const search = useSuperSearchQuery(apiQuery, isSearchEnabled, {
    contextCompanyId: context.company?.id,
    contextFirmId: context.firm?.id,
    contextMeasurementDate: activeMeasurementDate,
    includeSavedDestinations,
    includeDocuments,
    requestKey,
  });

  // Rehydrate the route context and clear transient search state every time the palette opens.
  useEffect(() => {
    if (!open) {
      cancelPendingNavigation();
      return;
    }

    cancelPendingNavigation();
    setContext(initialContext);
    setPendingPage(undefined);
    setQuery('');
    setSelectedIndex(-1);
    setIsKeyboardNavigation(false);
    setPressedShortcut(undefined);
  }, [cancelPendingNavigation, initialContext, open]);

  // Clear delayed navigation and key feedback after the command palette unmounts.
  useEffect(
    () => () => {
      if (navigationTimerRef.current !== null) {
        window.clearTimeout(navigationTimerRef.current);
      }
      if (shortcutFeedbackTimerRef.current !== null) {
        window.clearTimeout(shortcutFeedbackTimerRef.current);
      }
    },
    []
  );

  const results = useMemo(() => {
    const availableResults = isSearchEnabled
      ? search.data?.results.filter(result => {
        if (!search.isFetching) {
          return true;
        }

        if (includeDocuments && result.type === SuperSearchResult.type.DOCUMENT) {
          return false;
        }

        return !includeSavedDestinations || !isSuperSearchSavedDestinationResult(result);
      })
      : undefined;
    const visibleResults = availableResults?.filter(
      result =>
        matchesSuperSearchQuery(normalizedQuery, result.search_text) &&
        (!result.page?.required_feature_flag || checkFeature(result.page.required_feature_flag))
    );
    const mergedResults = mergeSuperSearchResults(
      localPageResults,
      localEntityResults,
      localMeasurementDateResults,
      visibleResults
    );

    // Keep immediate navigation stable, then preserve API relevance while promoting pinned entities.
    return mergedResults.sort((left, right) => {
      const isLeftLocal = localResultIds.has(left.id);
      const isRightLocal = localResultIds.has(right.id);
      if (isLeftLocal !== isRightLocal) {
        return Number(isRightLocal) - Number(isLeftLocal);
      }

      const isLeftPinned = left.company ? pinnedCompanyIds.has(left.company.id) : pinnedFirmIds.has(left.firm.id);
      const isRightPinned = right.company ? pinnedCompanyIds.has(right.company.id) : pinnedFirmIds.has(right.firm.id);

      return Number(isRightPinned) - Number(isLeftPinned);
    });
  }, [
    checkFeature,
    includeDocuments,
    includeSavedDestinations,
    isSearchEnabled,
    localEntityResults,
    localMeasurementDateResults,
    localPageResults,
    localResultIds,
    normalizedQuery,
    pinnedCompanyIds,
    pinnedFirmIds,
    search.data?.results,
    search.isFetching,
  ]);
  const resultSections = useMemo(
    () =>
      getSuperSearchSections({
        activeMeasurementDate,
        context,
        localPageTypes,
        localResultIds,
        pendingPage,
        query: debouncedQuery,
        results,
      }),
    [activeMeasurementDate, context, debouncedQuery, localPageTypes, localResultIds, pendingPage, results]
  );
  const commandSections = useMemo(
    () =>
      getSuperSearchCommandSections({
        availableTours,
        companyPermissions: {
          canMutateCompany,
          canOpenProForma,
        },
        context,
        firmPermissions: {
          canAccessAuditLogs,
          canAccessFirmSettings,
          canAccessUserManagement,
          canCreateCompany,
          canCreateMeasurementDate,
          canExportExcel: actionVisibility.excelExport,
          canExportPdf: actionVisibility.pdfExport,
          canRunBulkActions: actionVisibility.bulkActions,
        },
        hasCompanies: Array.isArray(companyList) && companyList.length > 0,
        routeFirm: initialContext.firm,
        pendingPage: Boolean(pendingPage),
        query: normalizedQuery,
      }),
    [
      canAccessAuditLogs,
      canAccessFirmSettings,
      canAccessUserManagement,
      canCreateCompany,
      canCreateMeasurementDate,
      actionVisibility.bulkActions,
      actionVisibility.excelExport,
      actionVisibility.pdfExport,
      availableTours,
      canMutateCompany,
      canOpenProForma,
      companyList,
      context,
      initialContext.firm,
      normalizedQuery,
      pendingPage,
    ]
  );
  const matchingSections = useMemo(() => {
    const navigationSections =
      context.firm && !context.company
        ? [
          ...resultSections.filter(section => section.id === 'companies'),
          ...resultSections.filter(section => section.id !== 'companies'),
        ]
        : resultSections;

    // Preserve navigation-first discovery; commands remain searchable without displacing companies and pages.
    return [...navigationSections, ...commandSections];
  }, [commandSections, context.company, context.firm, resultSections]);
  const matchingItems = useMemo(() => matchingSections.flatMap(section => section.items), [matchingSections]);
  const quickAccessSections = useMemo(() => {
    const previewSections = getSuperSearchSections({
      activeMeasurementDate,
      context,
      localPageTypes: quickAccessPageTypes,
      localResultIds: quickAccessResultIds,
      pendingPage,
      query: '',
      results: quickAccessResults,
    });
    const previewItems = previewSections
      .flatMap(section => section.items.slice(0, QUICK_ACCESS_RESULTS_PER_SECTION))
      .slice(0, QUICK_ACCESS_RESULT_LIMIT);

    if (previewItems.length === 0) {
      return [];
    }

    return [
      {
        id: 'quick-access',
        items: previewItems,
        label: 'Quick access',
      },
    ];
  }, [activeMeasurementDate, context, pendingPage, quickAccessPageTypes, quickAccessResultIds, quickAccessResults]);
  const isWaitingForRemoteSearch = open && isQueryDebouncing && normalizedQuery.length >= MIN_REMOTE_SEARCH_LENGTH;
  const isSearching = isWaitingForRemoteSearch || (isSearchEnabled && search.isFetching);
  const isLoading = isSearching && matchingItems.length === 0;
  const hasError = isSearchEnabled && search.isError && matchingItems.length === 0;
  const isShowingQuickAccess =
    normalizedQuery.length > 0 &&
    !isLoading &&
    !hasError &&
    matchingItems.length === 0 &&
    quickAccessSections.length > 0;
  const visibleSections = isShowingQuickAccess ? quickAccessSections : matchingSections;
  const items = useMemo(() => visibleSections.flatMap(section => section.items), [visibleSections]);
  const previousItemsRef = useRef(items);
  const itemIndexById = useMemo(() => new Map(items.map((item, index) => [item.id, index])), [items]);
  const itemToneById = useMemo(
    () =>
      new Map(
        visibleSections.flatMap(section =>
          section.items.map(item => [item.id, getResultTone(item, section.id)] as const)
        )
      ),
    [visibleSections]
  );
  const selectedItem = items[selectedIndex];
  const ghostCompletion = getGhostCompletion(query, selectedItem);
  let tabActionLabel: 'complete' | 'set scope' | undefined;
  if (selectedItem && canScopeItem(selectedItem, pendingPage)) {
    tabActionLabel = 'set scope';
  } else if (selectedItem && ghostCompletion) {
    tabActionLabel = 'complete';
  }
  const shouldShowSearchGuidance = normalizedQuery.length > 0 && normalizedQuery.length < MIN_REMOTE_SEARCH_LENGTH;
  const resultsAnnouncement = getResultsAnnouncement({
    hasError,
    isLoading,
    quickAccessCount: isShowingQuickAccess ? items.length : 0,
    resultCount: matchingItems.length,
  });
  let emptyTitle = 'No available destinations';
  let emptyDescription = 'There are no destinations available in the current context.';
  if (normalizedQuery.length > 0 && normalizedQuery.length < MIN_REMOTE_SEARCH_LENGTH) {
    emptyTitle = 'Continue typing to search more';
    emptyDescription = `No local matches for “${normalizedQuery}” yet. Continue typing to search versions, saved views, valuation approaches, and documents.`;
  } else if (normalizedQuery.length >= MIN_REMOTE_SEARCH_LENGTH) {
    emptyTitle = 'No exact matches';
    emptyDescription = `No results matched “${normalizedQuery}”. Try a broader search or check the spelling.`;
  }
  const isContextDimmed = Boolean(
    selectedItem?.isOutsideContext || (items.length > 0 && items.every(item => item.isOutsideContext))
  );
  let searchPlaceholder = 'Search firms, companies, actions, documents, versions, views, pages, and dates...';
  if (pendingPage) {
    searchPlaceholder = `Choose a ${pendingPage.scope === SuperSearchPageResult.scope.FIRM ? 'firm' : 'company'} for ${pendingPage.label}`;
  } else if (context.company) {
    searchPlaceholder = `Search ${context.company.name} documents, versions, views, approaches, pages, and dates...`;
  } else if (context.firm) {
    searchPlaceholder = `Search ${context.firm.name} actions, settings, companies, documents, versions, views, and dates...`;
  }

  // Keep the active result stable when remote sections are appended after the local preview.
  useEffect(() => {
    const previousItems = previousItemsRef.current;
    previousItemsRef.current = items;
    setSelectedIndex(currentIndex => {
      if (currentIndex < 0) {
        return -1;
      }

      const previousSelectedId = previousItems[currentIndex]?.id;
      if (previousSelectedId) {
        return itemIndexById.get(previousSelectedId) ?? -1;
      }

      return currentIndex < items.length ? currentIndex : -1;
    });
  }, [itemIndexById, items]);

  const handleRetrySearch = useCallback(() => {
    searchInputRef.current?.focus();
    search.refetch().catch(() => undefined);
  }, [search]);
  let broaderSearchLabel: string | undefined;
  if (!pendingPage && context.company && context.firm) {
    broaderSearchLabel = `Search across ${context.firm.name}`;
  } else if (!pendingPage && context.firm) {
    broaderSearchLabel = 'Search across all firms';
  }
  const handleBroadenSearch = useCallback(() => {
    if (context.company && context.firm) {
      setContext({ firm: context.firm });
    } else if (context.firm) {
      setContext({});
    }

    setSelectedIndex(-1);
    setIsKeyboardNavigation(false);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [context.company, context.firm]);
  const handleClearSearch = useCallback(() => {
    setQuery('');
    setSelectedIndex(-1);
    setIsKeyboardNavigation(false);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  // A new query or scope returns the list to the top without preselecting a result.
  useEffect(() => {
    resultsRootRef.current?.scrollTo({ top: 0 });
  }, [context.company?.id, context.firm?.id, pendingPage?.key, query]);

  // Ensure keyboard navigation never leaves the highlighted result outside the scroll viewport.
  useEffect(() => {
    if (!isKeyboardNavigation) {
      return;
    }

    selectedItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isKeyboardNavigation, selectedIndex]);

  // Move focus to the only available action while delayed navigation remains cancellable.
  useEffect(() => {
    if (pendingNavigation) {
      navigationCancelRef.current?.focus();
    }
  }, [pendingNavigation]);

  const handleClose = useCallback(() => {
    cancelPendingNavigation();
    onClose();
  }, [cancelPendingNavigation, onClose]);

  const handleCancelNavigation = useCallback(() => {
    cancelPendingNavigation();
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [cancelPendingNavigation]);

  const handleDialogClose: NonNullable<DialogProps['onClose']> = useCallback(
    (_event, reason) => {
      if (reason !== 'escapeKeyDown' || !pendingNavigation) {
        handleClose();
        return;
      }

      handleCancelNavigation();
    },
    [handleCancelNavigation, handleClose, pendingNavigation]
  );

  const handleNavigate = useCallback(
    (item: SuperSearchDisplayItem) => {
      const targetCompany = item.result.company
        ? (companyList as FirmCompany[] | null)?.find(company => company.company_id === item.result.company?.id)
        : undefined;
      const targetCompanyMeasurementDates = targetCompany?.company_measurement_dates ?? [];
      const eligibleMeasurementDates =
        item.page?.key === VALUATIONS_SLUG
          ? targetCompanyMeasurementDates.filter(({ allocation_ids }) => Boolean(allocation_ids?.length))
          : targetCompanyMeasurementDates;
      const latestTargetMeasurementDate = eligibleMeasurementDates.reduce<string | undefined>(
        (latestDate, { date }) => (!latestDate || date > latestDate ? date : latestDate),
        undefined
      );
      const actionMeasurementDate = item.companyAction
        ? (eligibleMeasurementDates.find(({ date }) => date === activeMeasurementDate)?.date ??
          latestTargetMeasurementDate ??
          activeMeasurementDate)
        : undefined;
      const path = getSuperSearchNavigationPath({
        currentPathname: location.pathname,
        currentSearch: location.search,
        fallbackMeasurementDate: actionMeasurementDate,
        initialContext,
        item,
      });
      cancelPendingNavigation();
      setPendingNavigation({
        context: getNavigationContext(item),
        label: item.label,
        tone: itemToneById.get(item.id) ?? getResultTone(item, ''),
        typeLabel: getResultTypeLabel(item),
      });
      navigationTimerRef.current = window.setTimeout(() => {
        navigationTimerRef.current = null;
        handleClose();
        navigate(path);
      }, NAVIGATION_DELAY_MS);
    },
    [
      activeMeasurementDate,
      cancelPendingNavigation,
      companyList,
      handleClose,
      initialContext,
      itemToneById,
      location.pathname,
      location.search,
      navigate,
    ]
  );

  const handleScopeItem = useCallback(
    (item: SuperSearchDisplayItem) => {
      if (item.kind === 'company-page-command' && item.page) {
        setPendingPage({
          key: item.page.key,
          label: item.page.label,
          scope: SuperSearchPageResult.scope.COMPANY,
        });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (item.kind === 'firm-page-command' && item.page) {
        setPendingPage({
          key: item.page.key,
          label: item.page.label,
          scope: SuperSearchPageResult.scope.FIRM,
        });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (item.result.type === SuperSearchResult.type.FIRM) {
        setContext({ firm: item.result.firm });
        setPendingPage(undefined);
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (item.result.type === SuperSearchResult.type.COMPANY && item.result.company) {
        setContext({ company: item.result.company, firm: item.result.firm });
        setPendingPage(undefined);
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (pendingPage?.scope === SuperSearchPageResult.scope.FIRM) {
        setContext({ firm: item.result.firm });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (pendingPage?.scope === SuperSearchPageResult.scope.COMPANY && item.result.company) {
        setContext({ company: item.result.company, firm: item.result.firm });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      return false;
    },
    [pendingPage]
  );

  const handleBaseAction = useSuperSearchActions({
    onClose: handleClose,
    onNavigate: handleNavigate,
    onScopeItem: handleScopeItem,
    onStartTour: startTour,
  });
  const handleActivateItem = useCallback(
    (item: SuperSearchDisplayItem) => {
      if (item.kind === 'company-action' && item.companyAction && item.page && !context.company) {
        setPendingPage({
          companyAction: item.companyAction,
          key: item.page.key,
          label: item.label,
          searchLabel: item.page.label,
          scope: SuperSearchPageResult.scope.COMPANY,
        });
        setQuery('');
        setSelectedIndex(-1);
        return;
      }

      handleBaseAction(item);
    },
    [context.company, handleBaseAction]
  );

  const removeLastContext = useCallback(() => {
    setSelectedIndex(-1);

    if (pendingPage) {
      setPendingPage(undefined);
      return;
    }

    if (context.company) {
      setContext(currentContext => ({ firm: currentContext.firm }));
      return;
    }

    if (context.firm) {
      setContext({});
    }
  }, [context.company, context.firm, pendingPage]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        flashShortcut('close');
        event.preventDefault();
        event.stopPropagation();

        if (pendingNavigation) {
          handleCancelNavigation();
          return;
        }

        handleClose();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        flashShortcut('navigate');
        event.preventDefault();
        const isStartingKeyboardNavigation = !isKeyboardNavigation;
        setIsKeyboardNavigation(true);
        setSelectedIndex(currentIndex => {
          if (items.length === 0) {
            return -1;
          }

          if (isStartingKeyboardNavigation || currentIndex === -1) {
            return event.key === 'ArrowDown' ? 0 : items.length - 1;
          }

          const direction = event.key === 'ArrowDown' ? 1 : -1;
          return (currentIndex + direction + items.length) % items.length;
        });
        return;
      }

      if (event.key === 'Enter' && selectedItem) {
        flashShortcut('go');
        event.preventDefault();
        handleActivateItem(selectedItem);
        return;
      }

      if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();

        if (selectedItem && handleScopeItem(selectedItem)) {
          flashShortcut('setScope');
          return;
        }

        if (ghostCompletion && selectedItem) {
          flashShortcut('setScope');
          setQuery(selectedItem.label);
        }
        return;
      }

      if (event.key === 'Backspace' && query === '') {
        flashShortcut('popScope');
        removeLastContext();
      }
    },
    [
      ghostCompletion,
      flashShortcut,
      handleActivateItem,
      handleCancelNavigation,
      handleClose,
      handleScopeItem,
      isKeyboardNavigation,
      items.length,
      pendingNavigation,
      query,
      removeLastContext,
      selectedItem,
    ]
  );

  const handleFirmContextClick = () => {
    setContext(currentContext => ({ firm: currentContext.firm }));
    setPendingPage(undefined);
    setQuery('');
    setSelectedIndex(-1);
  };

  const handleCompanyContextClick = () => {
    setPendingPage(undefined);
    setQuery('');
    setSelectedIndex(-1);
  };

  return (
    <PrismThemeProvider>
      <StyledDialog
        aria-label="Super Search"
        data-testid={SUPER_SEARCH_TEST_IDS.dialog}
        fullWidth
        isNavigating={Boolean(pendingNavigation)}
        maxWidth={false}
        onClose={handleDialogClose}
        open={open}
        zoomLevel={zoomLevel}>
        {isSearching && (
          <LoadingProgress aria-label="Loading Super Search results" data-testid={SUPER_SEARCH_TEST_IDS.loading} />
        )}
        {pendingNavigation && (
          <NavigationOverlay data-testid={SUPER_SEARCH_TEST_IDS.navigation}>
            <AssistiveText aria-atomic="true" aria-live="polite" role="status">
              Navigating to {pendingNavigation.label} in {pendingNavigation.context}. Press Escape to cancel.
            </AssistiveText>
            <NavigationIcon aria-hidden tone={pendingNavigation.tone}>
              <ArrowForward />
            </NavigationIcon>
            <NavigationTitle>Navigating to {pendingNavigation.typeLabel}</NavigationTitle>
            <NavigationTarget tone={pendingNavigation.tone}>{pendingNavigation.label}</NavigationTarget>
            <NavigationContext>{pendingNavigation.context}</NavigationContext>
            <NavigationProgress tone={pendingNavigation.tone} />
            <NavigationCancel
              aria-label={`Cancel navigation to ${pendingNavigation.label}`}
              onClick={handleCancelNavigation}
              ref={navigationCancelRef}>
              Cancel
              <KeyCap data-pressed={pressedShortcut === 'close' || undefined}>esc</KeyCap>
            </NavigationCancel>
          </NavigationOverlay>
        )}
        <SearchHeader>
          <SearchInputRow>
            <Search aria-hidden />
            {(context.firm || context.company || pendingPage) && (
              <ContextRow isDimmed={isContextDimmed}>
                {context.firm && (
                  <OverflowTooltip placement="top" title={context.firm.name}>
                    {context.company || pendingPage ? (
                      <ContextButton
                        aria-label={`Search within ${context.firm.name} and clear the ${context.company ? 'company scope' : 'pending page'}`}
                        onClick={handleFirmContextClick}
                        tone="firm">
                        {context.firm.name}
                      </ContextButton>
                    ) : (
                      <ContextCurrent tone="firm">{context.firm.name}</ContextCurrent>
                    )}
                  </OverflowTooltip>
                )}
                {context.company && (
                  <>
                    <ContextSeparator aria-hidden>
                      <ChevronRight />
                    </ContextSeparator>
                    <OverflowTooltip placement="top" title={context.company.name}>
                      {pendingPage ? (
                        <ContextButton
                          aria-label={`Search within ${context.company.name} and clear the pending page`}
                          onClick={handleCompanyContextClick}
                          tone="company">
                          {context.company.name}
                        </ContextButton>
                      ) : (
                        <ContextCurrent tone="company">{context.company.name}</ContextCurrent>
                      )}
                    </OverflowTooltip>
                  </>
                )}
                {pendingPage && (
                  <>
                    <ContextSeparator aria-hidden>
                      <ChevronRight />
                    </ContextSeparator>
                    <OverflowTooltip placement="top" title={pendingPage.label}>
                      <ContextCurrent tone="companyPage">{pendingPage.label}</ContextCurrent>
                    </OverflowTooltip>
                  </>
                )}
                <ContextSeparator aria-hidden>
                  <ChevronRight />
                </ContextSeparator>
              </ContextRow>
            )}
            <SearchTextField>
              <SearchInput
                autoFocus
                inputRef={searchInputRef}
                inputProps={{
                  'aria-activedescendant': selectedItem ? SUPER_SEARCH_TEST_IDS.result(selectedItem.id) : undefined,
                  'aria-autocomplete': 'list',
                  'aria-controls': SUPER_SEARCH_TEST_IDS.results,
                  'aria-describedby': SUPER_SEARCH_TEST_IDS.instructions,
                  'aria-expanded': open,
                  'aria-label': 'Search firms, companies, documents, pages, and measurement dates',
                  'data-testid': SUPER_SEARCH_TEST_IDS.input,
                  role: 'combobox',
                }}
                onChange={event => {
                  setQuery(event.target.value);
                  setSelectedIndex(-1);
                  setIsKeyboardNavigation(false);
                }}
                onBlur={() => setPressedShortcut(undefined)}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                value={query}
              />
              {ghostCompletion && (
                <GhostText aria-hidden>
                  <GhostPrefix>{query}</GhostPrefix>
                  {ghostCompletion}
                </GhostText>
              )}
              <AssistiveText id={SUPER_SEARCH_TEST_IDS.instructions}>
                Type to search. Use the Up and Down Arrow keys to navigate results, Tab to set scope when available,
                Enter to navigate, Backspace on an empty search to remove scope, and Escape to close.
              </AssistiveText>
            </SearchTextField>
          </SearchInputRow>
        </SearchHeader>

        <ResultsRoot aria-busy={isSearching} ref={resultsRootRef}>
          <AssistiveText
            aria-atomic="true"
            aria-live="polite"
            data-testid={SUPER_SEARCH_TEST_IDS.status}
            id={SUPER_SEARCH_TEST_IDS.status}
            role="status">
            {resultsAnnouncement}
          </AssistiveText>
          {isLoading && (
            <SearchLoadingState data-testid={SUPER_SEARCH_TEST_IDS.loadingState}>
              <SearchLoadingLabel>Searching available content...</SearchLoadingLabel>
              {SEARCH_SKELETON_ROWS.map(row => (
                <SearchSkeletonRow aria-hidden key={row}>
                  <SearchSkeletonIcon animation="wave" variant="rounded" />
                  <SearchSkeletonCopy>
                    <SearchSkeletonLine animation="wave" variant="rounded" width="34%" />
                    <SearchSkeletonLine animation="wave" variant="rounded" width="58%" />
                  </SearchSkeletonCopy>
                </SearchSkeletonRow>
              ))}
            </SearchLoadingState>
          )}
          {hasError && !isSearching && (
            <ErrorState data-testid={SUPER_SEARCH_TEST_IDS.error}>
              <ErrorIcon aria-hidden>
                <ErrorOutlineRounded />
              </ErrorIcon>
              <ErrorTitle>We couldn&apos;t load your results</ErrorTitle>
              <ErrorDescription>Check your connection and try the search again.</ErrorDescription>
              <ErrorRetryButton data-testid={SUPER_SEARCH_TEST_IDS.retry} onClick={handleRetrySearch}>
                <RefreshRounded aria-hidden />
                Retry
              </ErrorRetryButton>
            </ErrorState>
          )}
          {shouldShowSearchGuidance && matchingItems.length > 0 && !hasError && (
            <SearchGuidance>
              Continue typing to include versions, saved views, valuation approaches, and documents.
            </SearchGuidance>
          )}
          {!isLoading && !hasError && matchingItems.length === 0 && (
            <EmptyState data-testid={SUPER_SEARCH_TEST_IDS.empty}>
              <EmptyIcon aria-hidden>
                <SearchOffRounded />
              </EmptyIcon>
              <EmptyTitle>{emptyTitle}</EmptyTitle>
              <EmptyDescription>{emptyDescription}</EmptyDescription>
              {normalizedQuery.length > 0 && (
                <EmptyActions>
                  {broaderSearchLabel && (
                    <EmptyActionButton data-testid={SUPER_SEARCH_TEST_IDS.broaden} onClick={handleBroadenSearch}>
                      {broaderSearchLabel}
                    </EmptyActionButton>
                  )}
                  <EmptyActionButton data-testid={SUPER_SEARCH_TEST_IDS.clear} onClick={handleClearSearch}>
                    Clear search
                  </EmptyActionButton>
                </EmptyActions>
              )}
            </EmptyState>
          )}
          <ResultsList
            aria-label="Super Search results"
            data-testid={SUPER_SEARCH_TEST_IDS.results}
            id={SUPER_SEARCH_TEST_IDS.results}
            role="listbox">
            {!isLoading &&
              !hasError &&
              visibleSections.map(section => {
                const sectionLabelId = SUPER_SEARCH_TEST_IDS.sectionLabel(section.id);

                return (
                  <SectionRoot aria-labelledby={sectionLabelId} key={section.id} role="group">
                    <SectionLabel id={sectionLabelId} tone={getSectionTone(section.id)}>
                      {section.label}
                    </SectionLabel>
                    {section.items.map(item => {
                      const itemIndex = itemIndexById.get(item.id) ?? 0;
                      const isSelected = itemIndex === selectedIndex;
                      const subtitleSegments = getOrderedSubtitleSegments(item);
                      const resultTone = getResultTone(item, section.id);

                      return (
                        <ResultButton
                          aria-label={`${item.label}. ${subtitleSegments
                            .map(({ segment }) => segment)
                            .join(' / ')}. ${getResultTypeLabel(item)}`}
                          aria-selected={isSelected}
                          data-testid={SUPER_SEARCH_TEST_IDS.result(item.id)}
                          id={SUPER_SEARCH_TEST_IDS.result(item.id)}
                          isSelected={isSelected}
                          key={item.id}
                          onClick={() => handleActivateItem(item)}
                          onMouseMove={() => {
                            setIsKeyboardNavigation(false);
                            setSelectedIndex(itemIndex);
                          }}
                          ref={isSelected ? selectedItemRef : undefined}
                          role="option"
                          tabIndex={-1}>
                          <ResultIcon aria-hidden tone={resultTone}>
                            {getSuperSearchResultIcon(item, section.id, firmIconUrlsById)}
                          </ResultIcon>
                          <ResultCopy>
                            <OverflowTooltip placement="top-start" title={item.label}>
                              <ResultLabel>{item.label}</ResultLabel>
                            </OverflowTooltip>
                            <ResultSubtitle isSelected={isSelected}>
                              {subtitleSegments.map(({ originalIndex, segment, tone: subtitleTone }) => (
                                <OverflowTooltip
                                  key={`${item.id}:subtitle:${originalIndex}`}
                                  placement="top-start"
                                  title={segment}>
                                  <ResultSubtitleSegment
                                    preserveWidth={
                                      item.result.type === SuperSearchResult.type.VALUATION_APPROACH &&
                                      originalIndex === 0
                                    }
                                    tone={subtitleTone}>
                                    {segment}
                                  </ResultSubtitleSegment>
                                </OverflowTooltip>
                              ))}
                            </ResultSubtitle>
                          </ResultCopy>
                          <ResultMeta aria-hidden>
                            <ResultTypeBadge tone={resultTone}>{getResultTypeLabel(item)}</ResultTypeBadge>
                            {isSelected && <KeyCap>↵</KeyCap>}
                          </ResultMeta>
                        </ResultButton>
                      );
                    })}
                  </SectionRoot>
                );
              })}
          </ResultsList>
        </ResultsRoot>

        <SuperSearchFooter context={context} pressedShortcut={pressedShortcut} tabActionLabel={tabActionLabel} />
      </StyledDialog>
    </PrismThemeProvider>
  );
};

export default SuperSearch;
